import { Injectable, inject } from '@angular/core';
import {
  ConfirmationService,
  TableDataPayload,
  TableLazyLoad,
  ToastService,
} from '@web/shared/ui';
import { UserCollectionFilter } from '../models/user-collection-filter';
import { map, Observable, tap } from 'rxjs';
import { UserCollectionItem } from '../models/user-collection-item';
import { UserApiService } from '../api/services/user-api.service';
import { CollectionRequestBuilder } from '@web/common';
import { blank, Guid, once } from '@web/utils';
import { User } from '../models/user';
import { UserFormData } from '../models/user-form-data';
import { UserUpdateRequest } from '../api/requests/user-update.request';
import { UserCreateRequest } from '../api/requests/user-create.request';
import { isUnavailableRole } from '../utils/is-unavailable-role';

@Injectable({
  providedIn: 'root',
})
export class UserDataSourceService {
  private readonly userApi = inject(UserApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toast = inject(ToastService);

  public fetchCollection(
    lazyLoad: TableLazyLoad<UserCollectionFilter>,
  ): Observable<TableDataPayload<UserCollectionItem>> {
    /* prettier-ignore */
    return this.userApi
      .fetchCollection(
        this.collectionRequestBuilder
          .page(lazyLoad.page)
          .pageSize(lazyLoad.rows)
          .sorts((builder) =>
            builder.fromTableSort(lazyLoad.sort).buildAsArray(),
          )
          .filters((builder) =>
            builder
              .fromTableFilter(lazyLoad.filter)
              .appendDate((builder) => builder.rangeFor('activatedAt').build())
              .appendDate((builder) => builder.rangeFor('createdAt').build())
              .appendDate((builder) => builder.rangeFor('lastLoginAt').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('email').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('firstName').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('displayName').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('lastName').build())
              .appendText((builder) => builder.inFor('accountType').build())
              .appendText((builder) => builder.inFor('roles').build())
              .appendText((builder) => builder.inFor('status').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('username').build())
              .buildAsArray(),
          )
          .build(),
      )
      .pipe(
        map(
          (response) =>
            new TableDataPayload(
              response.items.map(
                (item) =>
                  /* prettier-ignore */
                  new UserCollectionItem({
                    accountType: item.accountType,
                    activatedAt: item.activatedAt,
                    canActivationMessageBeResent: item.contextOperations.canActivationMessageBeResent,
                    canBeActivated: item.contextOperations.canBeActivated,
                    canBeEdited: item.contextOperations.canBeEdited,
                    canBeRemoved: item.contextOperations.canBeDeleted,
                    canBeViewed: item.contextOperations.canBeViewed,
                    createdAt: item.createdAt,
                    email: item.email,
                    firstName: item.firstName,
                    displayName: item.displayName,
                    id: item.id,
                    lastLoginAt: item.lastLoginAt,
                    lastName: item.lastName,
                    roles: item.roles,
                    status: item.status,
                    username: item.username,
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  public removeById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz usunąć wybranego użytkownika?')
          .accept(() => {
            this.userApi
              .removeById(id)
              .pipe(
                once(),
                tap(() => {
                  this.toast.show((builder) =>
                    builder
                      .initSuccess(
                        'Wybrany użytkownik został usunięty pomyślnie.',
                      )
                      .build(),
                  );
                }),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public fetchDetailsById(id: Guid): Observable<User> {
    return this.userApi.fetchDetailsById(id).pipe(
      map(
        (response) =>
          new User({
            accountType: response.accountType,
            activatedAt: response.activatedAt,
            canBeEdited: response.contextOperations.canBeEdited,
            canBeRemoved: response.contextOperations.canBeDeleted,
            canBeViewed: response.contextOperations.canBeViewed,
            createdAt: response.createdAt,
            displayName: response.displayName,
            email: response.email,
            firstName: response.firstName,
            id: response.id,
            lastLoginAt: response.lastLoginAt,
            lastPasswordChangeAt: response.lastPasswordChangeAt,
            lastName: response.lastName,
            roles: response.roles,
            status: response.status,
            username: response.username,
          }),
      ),
    );
  }

  public fetchDetailsFormById(id: Guid): Observable<UserFormData> {
    return this.userApi.fetchDetailsById(id).pipe(
      map(
        (response) =>
          new UserFormData({
            displayName: response.displayName,
            email: response.email,
            firstName: response.firstName,
            lastName: response.lastName,
            newsletter: response.newsletter,
            roles: response.roles.filter((role) => !isUnavailableRole(role)),
            username: response.username,
          }),
      ),
    );
  }

  public updateByFormData(id: Guid, data: UserFormData): Observable<void> {
    return this.userApi
      .update(
        id,
        new UserUpdateRequest({
          displayName: data.displayName,
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          newsletter: data.newsletter,
          password: data.password,
          roles: data.roles,
          username: data.username,
        }),
      )
      .pipe(
        tap(() => {
          this.toast.show((builder) =>
            builder
              .initSuccess('Użytkownik został zaktualizowany pomyślnie.')
              .build(),
          );
        }),
        blank(),
      );
  }

  public createByFormData(data: UserFormData): Observable<void> {
    return this.userApi.create(
      new UserCreateRequest({
        firstName: data.firstName,
        lastName: data.lastName,
        displayName: data.displayName,
        username: data.username,
        email: data.email,
        roles: data.roles,
        newsletter: data.newsletter,
        password: data.password,
      }),
    );
  }

  public resendActivationMessageById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message(
            'Czy na pewno chcesz ponownie wysłać e-mail z linkiem aktywacyjnym?',
          )
          .accept(() => {
            this.userApi
              .resendActivationMessage(id)
              .pipe(
                once(),
                tap(() => {
                  this.toast.show((builder) =>
                    builder
                      .initSuccess(
                        'E-mail z linkiem aktywacyjnym został wysłany.',
                      )
                      .build(),
                  );
                }),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public activate(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz aktywować wybranego użytkownika?')
          .accept(() => {
            this.userApi
              .activate(id)
              .pipe(
                once(),
                tap(() => {
                  this.toast.show((builder) =>
                    builder
                      .initSuccess('Wybrane konto zostało aktywowane.')
                      .build(),
                  );
                }),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }
}
